<template>
  <div v-if="panelVisible && sessionCount > 0" class="download-session-panel">
    <div class="panel-header">
      <q-icon name="cloud_download" size="sm" class="q-mr-xs" />
      <span class="panel-title">Download Sessions</span>
      <q-space />
      <q-btn flat dense round icon="close" size="sm" @click="closePanel" />
    </div>

    <div class="sessions-list q-px-sm q-pt-xs">
      <div
        v-for="s in allSessions"
        :key="s.downloadSessionId"
        class="session-card q-mb-xs q-pa-xs"
      >
        <div class="row items-center q-mb-xs">
          <q-icon
            :name="sessionStateIcon(s)"
            :color="sessionStateColor(s)"
            size="xs"
            class="q-mr-xs"
          />
          <span
            class="text-caption text-weight-medium"
            :class="`text-${sessionStateColor(s)}`"
          >
            {{ sessionStateLabel(s) }} &mdash; UID {{ s.uid }}
          </span>
          <q-space />
          <q-btn
            flat
            dense
            round
            icon="history"
            size="xs"
            class="text-grey-7"
            @click="openDeviceHistoryForSession(s)"
          >
            <q-tooltip>Device History</q-tooltip>
          </q-btn>
        </div>

        <div
          v-if="s.downloadSessionId < 0"
          class="text-caption text-grey-6 q-mb-xs row items-center"
        >
          <q-spinner color="grey-6" size="14px" class="q-mr-xs" />
          Waiting for the device to respond
        </div>
        <template v-else>
          <q-linear-progress
            :value="s.percentComplete / 100"
            :color="sessionProgressColor(s)"
            :stripe="s.state === 'T'"
            :animation-speed="s.state === 'T' ? 300 : 0"
            rounded
            size="18px"
            class="q-mb-xs progress-bar"
            :class="{ 'progress-bar--active': s.state === 'T' }"
          >
            <div class="absolute-full flex flex-center">
              <q-badge
                color="white"
                text-color="dark"
                :label="`${s.percentComplete}%`"
                :class="{ 'badge-pulse': s.state === 'T' }"
              />
            </div>
          </q-linear-progress>
          <div class="text-caption text-grey-6" style="font-size: 10px">
            {{ formatDate(s.startDate) }} &mdash; {{ formatDate(s.endDate) }}
            <span v-if="s.passageCount > 0" class="q-ml-xs text-grey-8">
              ({{ s.passageCount }} passages)
            </span>
          </div>
          <div
            v-if="
              s.latestPassageTimestamp && (s.state === 'T' || s.state === 'D')
            "
            class="text-caption text-light-blue"
            style="font-size: 10px"
          >
            <q-icon name="schedule" size="12px" class="q-mr-xs" />
            Latest downloaded timestamp:
            {{ formatDate(s.latestPassageTimestamp) }}
          </div>
          <div v-if="s.state === 'X'" class="text-caption text-red">
            Timed out &mdash; device may be offline.
          </div>
          <div v-else-if="s.state === 'A'" class="text-caption text-orange">
            Cancelled &mdash; superseded by a newer download or stop command.
          </div>
        </template>
        <div
          v-if="s.message"
          class="text-caption text-grey-7 q-mt-xs session-message"
        >
          <q-icon name="info" size="12px" class="q-mr-xs" />
          {{ s.message }}
        </div>
      </div>
    </div>

    <div v-if="allPassages.length > 0" class="passage-section q-px-sm q-pb-sm">
      <div class="row items-center q-mb-xs">
        <span class="text-caption text-weight-medium text-grey-7">
          Passages ({{ allPassages.length }})
        </span>
      </div>
      <q-table
        v-if="allPassages.length > 0"
        :rows="allPassages"
        :columns="passageColumns"
        row-key="passageId"
        dense
        flat
        :rows-per-page-options="[5, 10, 25]"
        class="passage-table"
      >
        <template #body-cell-uid="props">
          <q-td :props="props">{{ props.row.uid }}</q-td>
        </template>
        <template #body-cell-graph-data="props">
          <q-td :props="props">
            <span v-if="(props.row.passageWaveforms || []).length === 1">
              <q-btn
                v-for="wf in props.row.passageWaveforms"
                :key="wf.passageWaveformId"
                size="sm"
                class="q-mr-xs btn-brand"
                dense
                icon="show_chart"
                :label="getWaveformLabel(wf.passageWaveformType)"
                @click="showChart(props, wf)"
              />
            </span>
            <span v-else-if="(props.row.passageWaveforms || []).length > 1">
              <q-btn-dropdown
                size="sm"
                class="q-mr-xs btn-brand"
                dense
                icon="show_chart"
              >
                <q-list dense>
                  <q-item
                    v-for="wf in props.row.passageWaveforms"
                    :key="wf.passageWaveformId"
                    v-close-popup
                    clickable
                    @click="showChart(props, wf)"
                  >
                    <q-item-section>
                      <q-item-label>{{
                        getWaveformLabel(wf.passageWaveformType)
                      }}</q-item-label>
                    </q-item-section>
                  </q-item>
                </q-list>
              </q-btn-dropdown>
            </span>
          </q-td>
        </template>
        <template #body-cell-machine-learning-data="props">
          <q-td :props="props">
            <template
              v-for="wf in props.row.passageWaveforms || []"
              :key="wf.passageWaveformId"
            >
              <MLBadge
                :type="wf.passageWaveformType"
                :prediction="wf.mlPrediction"
                :confidence="wf.mlConfidence"
                :results="wf.machineLearningResults"
              />
            </template>
          </q-td>
        </template>
      </q-table>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import DeviceService from '@/services/DeviceService';
import { IDownloadSession } from '@/models/DownloadSession';
import { WaveformTypeLabel } from '@/models/PassageWaveform';
import MLBadge from '@/components/MLBadge.vue';
import { EventBus } from '@/utils/EventBus';

export default defineComponent({
  name: 'DownloadSessionPanel',
  components: { MLBadge },

  data() {
    return {
      unsubscribe: null as (() => void) | null,
      refreshDebounceTimer: null as ReturnType<typeof setTimeout> | null,
      refreshMaxWaitTimer: null as ReturnType<typeof setTimeout> | null,
      passageColumns: [
        {
          name: 'uid',
          label: 'UID',
          field: 'uid',
          sortable: true,
        },
        {
          name: 'passageTimeStamp',
          label: 'Timestamp',
          field: 'passageTimeStamp',
          sortable: true,
        },
        {
          name: 'detectionCount',
          label: 'Count',
          field: 'detectionCount',
          sortable: false,
        },
        {
          name: 'detectionFrequency',
          label: 'Frequency',
          field: 'detectionFrequency',
          sortable: false,
          format: (val: number) => (val === 0 ? 'MAG' : val + ' Hz'),
        },
        {
          name: 'graph-data',
          label: 'Data',
          field: 'passageId',
        },
        {
          name: 'machine-learning-data',
          label: 'ML',
          field: 'passageId',
          align: 'center',
        },
        {
          name: 'latitude',
          label: 'Lat',
          field: 'latitude',
          sortable: false,
          format: (val: number) =>
            val != null ? Math.round(val * 1e6) / 1e6 : '',
        },
        {
          name: 'longitude',
          label: 'Lon',
          field: 'longitude',
          sortable: false,
          format: (val: number) =>
            val != null ? Math.round(val * 1e6) / 1e6 : '',
        },
      ] as any[],
    };
  },

  computed: {
    panelVisible(): boolean {
      return this.$store.state.downloadSession.panelVisible;
    },
    sessionCount(): number {
      return Object.keys(this.$store.state.downloadSession.sessions).length;
    },
    allSessions(): IDownloadSession[] {
      return this.$store.getters.activeDownloadSessionList;
    },
    allPassages(): any[] {
      const seen = new Set<number>();
      const combined: any[] = [];
      for (const s of this.allSessions) {
        for (const p of s.passages || []) {
          if (!seen.has(p.passageId)) {
            seen.add(p.passageId);
            combined.push(p);
          }
        }
      }
      combined.sort(
        (a, b) =>
          new Date(b.passageTimeStamp).getTime() -
          new Date(a.passageTimeStamp).getTime()
      );
      return combined;
    },
  },

  created() {
    // Subscribe to the signal-only action dispatched by Hubs.ts.
    // This avoids deep-watching store state (performance rule #1).
    this.unsubscribe = this.$store.subscribeAction((action: any) => {
      if (action.type === 'downloadSessionStateReceived') {
        this.onDownloadSessionUpdate(action.payload);
      }
      if (action.type === 'mlResultReceived') {
        this.onMlResultReceived(action.payload);
      }
    });
  },

  mounted() {
    EventBus.on('switch-passage', this.handleSwitchPassage as any);
  },

  beforeUnmount() {
    if (this.unsubscribe) {
      this.unsubscribe();
    }
    this.clearTimers();
    EventBus.off('switch-passage', this.handleSwitchPassage as any);
  },

  methods: {
    sessionStateIcon(s: IDownloadSession): string {
      if (s.state === 'T' || s.state === 'D') return 'sync';
      if (s.state === 'C') return 'check_circle';
      if (s.state === 'A') return 'cancel';
      return 'error_outline';
    },

    sessionStateColor(s: IDownloadSession): string {
      if (s.state === 'T' || s.state === 'D') return 'light-blue';
      if (s.state === 'C') return 'green';
      if (s.state === 'A') return 'orange';
      return 'red';
    },

    sessionStateLabel(s: IDownloadSession): string {
      if (s.state === 'T' || s.state === 'D') return 'Downloading...';
      if (s.state === 'C') return 'Complete';
      if (s.state === 'A') return 'Cancelled';
      return 'Timed Out';
    },

    sessionProgressColor(s: IDownloadSession): string {
      if (s.state === 'C') return 'green';
      if (s.state === 'X') return 'red';
      if (s.state === 'A') return 'orange';
      return 'light-blue';
    },

    closePanel() {
      this.clearTimers();
      this.$store.commit('CLEAR_ALL_DOWNLOAD_SESSIONS');
    },

    onDownloadSessionUpdate(data: any) {
      this.scheduleFetch(data.downloadSessionId);
    },

    onMlResultReceived(data: any) {
      for (const s of this.allSessions) {
        if (s.downloadSessionId < 0) continue;
        if (data?.uids?.includes(s.uid)) {
          this.scheduleFetch(s.downloadSessionId);
        }
      }
    },

    scheduleFetch(sessionId: number) {
      // Trailing debounce: reset on every call
      if (this.refreshDebounceTimer) {
        clearTimeout(this.refreshDebounceTimer);
      }
      this.refreshDebounceTimer = setTimeout(() => {
        this.fetchPassagesForSession(sessionId);
        this.clearTimers();
      }, 1500);

      // Max-wait: only set once per burst
      if (!this.refreshMaxWaitTimer) {
        this.refreshMaxWaitTimer = setTimeout(() => {
          if (this.refreshDebounceTimer) {
            clearTimeout(this.refreshDebounceTimer);
          }
          this.fetchPassagesForSession(sessionId);
          this.clearTimers();
        }, 8000);
      }
    },

    clearTimers() {
      if (this.refreshDebounceTimer) {
        clearTimeout(this.refreshDebounceTimer);
        this.refreshDebounceTimer = null;
      }
      if (this.refreshMaxWaitTimer) {
        clearTimeout(this.refreshMaxWaitTimer);
        this.refreshMaxWaitTimer = null;
      }
    },

    fetchPassagesForSession(sessionId: number) {
      const session = this.$store.state.downloadSession.sessions[sessionId] as
        | IDownloadSession
        | undefined;
      if (!session) return;

      DeviceService.getDownloadSessionPassages(sessionId)
        .then((response) => {
          const data = response.data;
          const passages = (data.passageEventList || []).map((p: any) => ({
            passageId: p.passageId,
            uid: p.uid,
            passageTimeStamp: p.passageTimeStamp,
            detectionCount: p.detectionCount,
            detectionFrequency: p.detectionFrequency,
            latitude: p.latitude,
            longitude: p.longitude,
            passageWaveforms: p.passageWaveforms || [],
          }));
          this.$store.commit('SET_DOWNLOAD_SESSION_PASSAGES', {
            sessionId,
            passages,
          });
        })
        .catch((error) => {
          console.error('Failed to fetch passages for download session', error);
          this.$store.dispatch('notify', error);
        });
    },

    getWaveformLabel(type: string): string {
      return WaveformTypeLabel[type] || type;
    },

    getMLMean(prediction: boolean, confidence: number): number {
      return (2 * Number(prediction) - 1) * confidence;
    },

    getMLColor(prediction: boolean, confidence: number): string {
      const mean = this.getMLMean(prediction, confidence);
      if (mean > 0.89) return 'green';
      if (mean > 0.75) return 'green-4';
      if (mean > -0.25) return 'orange';
      if (mean > -0.5) return 'red-3';
      return 'red';
    },

    getMLIcon(color: string): string {
      if (color.includes('red')) return 'block';
      if (color.includes('green')) return 'check_circle_outline';
      return 'help';
    },

    getMLConfidenceLabel(prediction: boolean, confidence: number): string {
      const mean = this.getMLMean(prediction, confidence);
      return Math.round(50 * mean + 50) + '%';
    },

    showChart(props: any, wf: any) {
      this.$store.dispatch('setPassageDialogSource', 'historic_data');
      const passageCopy = { ...props.row };
      passageCopy.requestedWaveformId = wf.passageWaveformId;
      this.$store.dispatch('addPassageIdsToUpdate', [wf.passageWaveformId]);
      this.$store.dispatch('passageChartShow', passageCopy);
    },

    handleSwitchPassage({
      passage,
      direction,
      type,
    }: {
      passage: any;
      direction: 'next' | 'previous';
      type: string;
    }) {
      const passages = this.allPassages;
      if (!passages || passages.length === 0) return;

      const currentWaveformId =
        passage.requestedWaveformId || passage.passageId;

      const currentIndex = passages.findIndex((psg: any) =>
        (psg.passageWaveforms || []).some(
          (wf: any) => wf.passageWaveformId === currentWaveformId
        )
      );

      if (currentIndex === -1) return;

      const inc = direction === 'next' ? 1 : -1;
      const nextIndex =
        (currentIndex + inc + passages.length) % passages.length;
      const nextRow = passages[nextIndex];
      const nextWf =
        (nextRow.passageWaveforms || []).find(
          (wf: any) => wf.passageWaveformType === type
        ) || (nextRow.passageWaveforms || [])[0];
      if (nextWf) {
        this.showChart({ row: nextRow }, nextWf);
      }
    },

    openDeviceHistoryForSession(s: IDownloadSession) {
      const toQuasarDate = (iso: string) => {
        const d = new Date(iso);
        const y = d.getUTCFullYear();
        const m = String(d.getUTCMonth() + 1).padStart(2, '0');
        const day = String(d.getUTCDate()).padStart(2, '0');
        return `${y}/${m}/${day}`;
      };
      this.$store.dispatch('selectDevice', { uid: s.uid });
      this.$store.dispatch('historicDataShow', {
        fromDate: toQuasarDate(s.startDate),
        toDate: toQuasarDate(s.endDate),
      });
    },

    formatDate(dateStr: string): string {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      return d.toISOString().replace('T', ' ').substring(0, 19) + ' UTC';
    },
  },
});
</script>

<style scoped>
.download-session-panel {
  position: absolute;
  bottom: 12px;
  left: 12px;
  z-index: 1000;
  width: fit-content;
  min-width: 560px;
  max-width: 800px;
  max-height: 50vh;
  overflow-y: auto;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.22);
  display: flex;
  flex-direction: column;
}

.panel-header {
  display: flex;
  align-items: center;
  padding: 10px 14px 8px;
  background: #24337a;
  border-radius: 8px 8px 0 0;
  color: white;
  flex-shrink: 0;
}

.panel-title {
  font-weight: 600;
  font-size: 13px;
}

.sessions-list {
  max-height: 200px;
  overflow-y: auto;
}

.session-card {
  background: #f8f9fb;
  border-radius: 6px;
  border: 1px solid #e0e3ea;
}

.passage-section {
  margin-top: 4px;
  padding-top: 4px;
  border-top: 1px solid #e0e3ea;
}

.session-message {
  font-size: 11px;
}

.passage-table {
  max-height: 200px;
  font-size: 11px;
}

.passage-table :deep(.q-table__top),
.passage-table :deep(.q-table__bottom) {
  padding: 4px 8px;
}

.passage-table :deep(th),
.passage-table :deep(td) {
  padding: 2px 6px;
  font-size: 11px;
}

/* ── Progress bar shine + pulse ── */
.progress-bar {
  position: relative;
  overflow: hidden;
}

.progress-bar--active :deep(.q-linear-progress__track) {
  overflow: hidden;
}

.progress-bar--active::after {
  content: '';
  position: absolute;
  top: 0;
  left: -50%;
  width: 50%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.35),
    transparent
  );
  animation: shimmer 2s ease-in-out infinite;
  pointer-events: none;
  border-radius: inherit;
}

@keyframes shimmer {
  0% {
    left: -50%;
  }
  100% {
    left: 150%;
  }
}

.badge-pulse {
  animation: pulse 1.8s ease-in-out infinite;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.75;
    transform: scale(1.08);
  }
}
</style>
